import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { firmService, Firm } from '../services/firmService';
import { api, projectAPI } from '../services/api';
import { caseService } from '../services/caseService';
import { dueDateFromPriority, formatDueDate, PRIORITY_DUE_DATE_HELP } from '../utils/priorityDueDate';
import { parseBulkCaseList } from '../utils/bulkCaseParse';

interface Meta {
  statuses: Record<string, string>;
  priorities: Record<string, string>;
  types: Record<string, string>;
}

interface BulkRow {
  key: string;
  title: string;
}

const TERMINAL_STATUSES = ['delivered', 'closed', 'completed', 'mrr_delivered', 'mrr_completed', 'na'];

let rowKey = 0;
const nextKey = () => {
  rowKey += 1;
  return `row-${rowKey}`;
};

const BulkCaseCreate: React.FC = () => {
  const { firmId } = useParams();
  const [searchParams] = useSearchParams();
  const requestedProjectId = searchParams.get('project_id') || '';
  const navigate = useNavigate();

  const [meta, setMeta] = useState<Meta>({ statuses: {}, priorities: {}, types: {} });
  const [projects, setProjects] = useState<any[]>([]);
  const [firm, setFirm] = useState<Firm | null>(null);
  const [projectSettings, setProjectSettings] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [paste, setPaste] = useState('');
  const [rows, setRows] = useState<BulkRow[]>([]);
  const [skipped, setSkipped] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [created, setCreated] = useState<Array<{ id: number; title: string }>>([]);
  const [form, setForm] = useState({
    project_id: requestedProjectId,
    status: 'delivered',
    priority: 'normal',
    type: 'new',
    description: '',
    due_date: '',
    is_rush: false,
    notify_suppress: true,
  });

  const priorityBasedDueDate = projectSettings?.notification_settings?.priority_based_due_date === true;
  const dueDateRequired = !priorityBasedDueDate && projectSettings?.notification_settings?.allow_optional_due_date !== true;
  const statusNeedsDueDate = !TERMINAL_STATUSES.includes(form.status);
  const calculatedDueDate = priorityBasedDueDate ? dueDateFromPriority(form.priority || 'normal') : '';

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [metaRes, projectsRes, firmRes] = await Promise.all([
          api.get('/cases/meta', { params: requestedProjectId ? { project_id: requestedProjectId } : {} }),
          projectAPI.getAll({ firm_id: firmId }),
          firmId ? firmService.getFirmById(Number(firmId)) : Promise.resolve(null),
        ]);
        setMeta(metaRes.data.data);
        const data = projectsRes.data.data?.data || projectsRes.data.data || [];
        const list = Array.isArray(data) ? data : [];
        setProjects(list);
        if (firmRes) setFirm(firmRes.data);
        setForm((prev) => {
          if (prev.project_id && list.some((p: any) => String(p.id) === String(prev.project_id))) {
            return prev;
          }
          if (list.length === 1) {
            return { ...prev, project_id: String(list[0].id) };
          }
          return { ...prev, project_id: '' };
        });
      } catch {
        setError('Could not load projects for this firm.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [firmId]);

  useEffect(() => {
    if (!form.project_id) {
      setProjectSettings(null);
      return;
    }
    projectAPI.getSettings(Number(form.project_id))
      .then((res) => setProjectSettings(res.data.data || res.data))
      .catch(() => setProjectSettings(null));

    let cancelled = false;
    api.get('/cases/meta', { params: { project_id: form.project_id } })
      .then((res) => {
        if (cancelled) return;
        const next = res.data.data;
        setMeta(next);
        const codes = Object.keys(next?.statuses || {});
        setForm((prev) => {
          if (!codes.length || codes.includes(prev.status)) return prev;
          const fallback = ['delivered', 'open'].find((c) => codes.includes(c)) || codes[0];
          return { ...prev, status: fallback };
        });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [form.project_id]);

  const buildList = () => {
    const parsed = parseBulkCaseList(paste);
    setSkipped(parsed.skipped);
    setRows(parsed.cases.map((item) => ({
      key: nextKey(),
      title: item.title,
    })));
    setCreated([]);
    setError('');
  };

  const updateRow = (key: string, patch: Partial<BulkRow>) => {
    setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  };

  const handleCreate = async () => {
    const ready = rows
      .map((row) => ({ ...row, title: row.title.trim() }))
      .filter((row) => row.title);

    if (!form.project_id) {
      setError('Select the project these cases should be created in.');
      return;
    }
    if (ready.length === 0) {
      setError('Build a list with at least one case name.');
      return;
    }
    if (statusNeedsDueDate && dueDateRequired && !priorityBasedDueDate && !form.due_date) {
      setError('Due date is required for open cases in this project.');
      return;
    }

    setSaving(true);
    setError('');
    setCreated([]);
    try {
      const result = await caseService.bulkCreateCases({
        project_id: Number(form.project_id),
        status: form.status,
        priority: form.priority,
        is_rush: form.is_rush,
        type: form.type,
        description: form.description.trim() || undefined,
        due_date: statusNeedsDueDate && !priorityBasedDueDate && form.due_date ? form.due_date : undefined,
        notify_suppress: form.notify_suppress,
        cases: ready.map((row) => ({
          title: row.title,
        })),
      });
      setCreated(result?.data?.cases || []);
      setRows([]);
      setPaste('');
    } catch (err: any) {
      setError(err?.message || 'Failed to create cases');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate('/firms'); }} sx={{ textDecoration: 'none' }}>
          Firms
        </Link>
        {firm && (
          <Link color="inherit" href="#" onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}`); }} sx={{ textDecoration: 'none' }}>
            {firm.name}
          </Link>
        )}
        <Typography color="text.primary">Create cases</Typography>
      </Breadcrumbs>

      <Typography variant="h4" gutterBottom>Create cases</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Paste one case per line. The whole line is the case title, including a number at the start. File number is left blank. Each line is created as a new case in the project you select.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {created.length > 0 && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Created {created.length} case{created.length === 1 ? '' : 's'}.
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {created.map((item) => (
              <li key={item.id}>
                <Link href="#" onClick={(e) => { e.preventDefault(); navigate(`/firms/${firmId}/cases/${item.id}`); }}>
                  #{item.id} {item.title}
                </Link>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {loading ? (
        <CircularProgress />
      ) : (
        <Stack spacing={3}>
          <Card>
            <CardHeader title="Where to create them" subheader="Pick any project on this firm. Existing cases are not reused." />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Project"
                  select
                  required
                  value={form.project_id}
                  onChange={(e) => setForm((prev) => ({ ...prev, project_id: e.target.value }))}
                  fullWidth
                >
                  <MenuItem value="">- Select a project -</MenuItem>
                  {projects.map((project) => (
                    <MenuItem key={project.id} value={String(project.id)}>{project.name}</MenuItem>
                  ))}
                </TextField>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 2 }}>
                  <TextField
                    label="Status"
                    select
                    value={form.status}
                    onChange={(e) => setForm((prev) => ({ ...prev, status: e.target.value }))}
                    fullWidth
                  >
                    {Object.entries(meta.statuses).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Priority"
                    select
                    value={form.priority}
                    onChange={(e) => setForm((prev) => ({ ...prev, priority: e.target.value }))}
                    fullWidth
                  >
                    {Object.entries(meta.priorities).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    label="Type"
                    select
                    value={form.type}
                    onChange={(e) => setForm((prev) => ({ ...prev, type: e.target.value }))}
                    fullWidth
                  >
                    {Object.entries(meta.types).map(([key, label]) => (
                      <MenuItem key={key} value={key}>{label}</MenuItem>
                    ))}
                  </TextField>
                </Box>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.is_rush}
                      onChange={(e) => setForm((prev) => ({ ...prev, is_rush: e.target.checked }))}
                    />
                  )}
                  label="Rush"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1 }}>
                  Checked cases use the project RUSH rate. Priority stays separate.
                </Typography>
                {statusNeedsDueDate && (
                  priorityBasedDueDate ? (
                    <Box>
                      <Typography variant="body2">Due date: {formatDueDate(calculatedDueDate)}</Typography>
                      <Typography variant="caption" color="text.secondary">{PRIORITY_DUE_DATE_HELP}</Typography>
                    </Box>
                  ) : (
                    <TextField
                      label="Due Date"
                      type="date"
                      value={form.due_date}
                      onChange={(e) => setForm((prev) => ({ ...prev, due_date: e.target.value }))}
                      fullWidth
                      required={dueDateRequired}
                      InputLabelProps={{ shrink: true }}
                      helperText={dueDateRequired ? 'Required for this project' : 'Optional for this project'}
                    />
                  )
                )}
                <TextField
                  label="Description"
                  value={form.description}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                  fullWidth
                  multiline
                  minRows={2}
                  helperText="Applied to every case. For already-delivered consolidation work, you can note that here."
                />
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={form.notify_suppress}
                      onChange={(e) => setForm((prev) => ({ ...prev, notify_suppress: e.target.checked }))}
                    />
                  )}
                  label="Do not send notifications"
                />
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardHeader
              title="Case list"
              subheader="One case per line. The full line becomes the case title. Example: 89-Riley Bryan-Rush"
            />
            <Divider />
            <CardContent>
              <Stack spacing={2}>
                <TextField
                  label="Paste cases"
                  value={paste}
                  onChange={(e) => setPaste(e.target.value)}
                  fullWidth
                  multiline
                  minRows={8}
                  placeholder={'89-Riley Bryan-Rush\n103 Luk, Winona\n57 Padillas, Jasmine'}
                />
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={buildList} disabled={!paste.trim()}>
                    Build list
                  </Button>
                  <Button
                    variant="text"
                    onClick={() => setRows((prev) => [...prev, { key: nextKey(), title: '' }])}
                  >
                    Add row
                  </Button>
                </Box>
                {skipped.length > 0 && (
                  <Alert severity="warning">{skipped.join(' ')}</Alert>
                )}
                {rows.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Title</TableCell>
                        <TableCell width={56} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow key={row.key}>
                          <TableCell>
                            <TextField
                              value={row.title}
                              onChange={(e) => updateRow(row.key, { title: e.target.value })}
                              size="small"
                              fullWidth
                              required
                            />
                          </TableCell>
                          <TableCell>
                            <IconButton aria-label="Remove case" onClick={() => setRows((prev) => prev.filter((item) => item.key !== row.key))}>
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                <Typography variant="body2" color="text.secondary">
                  {rows.length} case{rows.length === 1 ? '' : 's'} ready
                  {form.is_rush ? ', marked Rush' : ''}.
                </Typography>
              </Stack>
            </CardContent>
          </Card>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={saving || rows.length === 0}>
              {saving ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Creating...
                </Box>
              ) : (
                `Create ${rows.length} case${rows.length === 1 ? '' : 's'}`
              )}
            </Button>
          </Box>
        </Stack>
      )}
    </Box>
  );
};

export default BulkCaseCreate;
